import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  SparklesIcon,
  PaperAirplaneIcon,
  UserCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { ChatBubbleLeftRightIcon } from "@heroicons/react/24/solid";
import {
  chatWithAITemplate,
  getAIConversations,
  suggestTemplate,
} from "../api/templates";
import { useAuth } from "../contexts/AuthContext";
import { usePageHeader } from "../contexts/PageHeaderContext";
import BreadCrumb from "../components/common/BreadCrumb";
import PagePermissionGuard from "../components/common/PagePermissionGuard";
import ChatMessageContent from "../components/common/ChatMessageContent";
import { getSignedUrl } from "../utils/signedUrl";
import { getDocument } from "../api/documents";

const CreateAITemplate = () => {
  const navigate = useNavigate();
  const { usage } = useAuth();
  const { setHeaderContent } = usePageHeader();
  const messagesEndRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [generatedDocument, setGeneratedDocument] = useState(null);
  const [generatedDocumentInfo, setGeneratedDocumentInfo] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [historyPagination, setHistoryPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 0 });
  const [loadingHistory, setLoadingHistory] = useState(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchConversations = async (page = 1) => {
    setLoadingHistory(true);
    try {
      const res = await getAIConversations({ page, limit: 10, status: "completed" });
      if (res?.success && res?.data) {
        setConversations(res.data);
        setHistoryPagination(res.pagination || { page: 1, limit: 10, total: 0, totalPages: 0 });
      }
    } catch (err) {
      toast.error(err?.message || "Failed to load conversation history");
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    if (historyOpen) fetchConversations(1);
  }, [historyOpen]);

  const formatHistoryDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    if (isToday) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    const isThisYear = d.getFullYear() === now.getFullYear();
    return isThisYear
      ? d.toLocaleDateString([], { month: "short", day: "numeric" })
      : d.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
  };

  const buildTemplateParamsFromResponse = async (data) => {
    const doc = data.document || data.documentId || null;
    const documentId =
      (doc && (doc._id || doc.id)) ||
      data.documentId ||
      data.document_id;

    if (!documentId) {
      return null;
    }

    let filePath = doc?.filePath || doc?.uploadUrl;
    let fileName = doc?.fileName || "Generated Document";

    if (!filePath) {
      try {
        const docResponse = await getDocument(documentId);
        const docData = docResponse.data || docResponse;
        filePath = docData.filePath || docData.uploadUrl;
        fileName = docData.fileName || fileName;
      } catch (err) {
        console.error("Error fetching document details:", err);
      }
    }

    let suggested = null;
    if (filePath) {
      try {
        const signedUrl = await getSignedUrl(filePath);
        const response = await fetch(signedUrl);
        if (response.ok) {
          const blob = await response.blob();
          const file = new File([blob], fileName || "document.pdf", {
            type: blob.type || "application/pdf",
          });
          const formData = new FormData();
          formData.append("file", file);
          const suggestResponse = await suggestTemplate(formData);
          suggested = suggestResponse.data || suggestResponse;
        }
      } catch (err) {
        console.error("Error getting template suggestions from /templates/suggest:", err);
      }
    }

    const gen = data.generatedDocument || {};
    const s = suggested || {};
    const params = new URLSearchParams();
    params.set("documentId", documentId);

    const documentType = s.documentType || gen.documentType || "";
    if (documentType) params.set("aiDocumentType", documentType);

    const templateName =
      s.templateName ||
      s.name ||
      gen.title ||
      gen.name ||
      "";
    if (templateName) params.set("aiTemplateName", templateName);

    const description =
      s.description ||
      data.description ||
      gen.summary ||
      gen.description ||
      gen.overview ||
      "";
    if (description) params.set("aiDescription", description);

    const emailSubject =
      s.emailSubject ||
      data.emailSubject ||
      gen.emailSubject ||
      "";
    if (emailSubject) params.set("aiEmailSubject", emailSubject);

    const emailMessage =
      s.emailMessage ||
      data.emailMessage ||
      gen.emailMessage ||
      gen.emailBody ||
      "";
    if (emailMessage) params.set("aiEmailMessage", emailMessage);

    return {
      documentId,
      filePath,
      fileName,
      params: params.toString(),
    };
  };

  const handleSendMessage = async () => {
    const trimmed = input.trim();
    if (!trimmed || isSending) return;

    if (usage?.exceededTemplateCreation) {
      toast.error(
        "You have reached the template creation limit for your plan. Please upgrade to create more templates."
      );
      return;
    }

    const newUserMessage = { role: "user", content: trimmed };
    const updatedMessages = [...messages, newUserMessage];
    setMessages(updatedMessages);
    setInput("");
    setIsSending(true);

    try {
      const response = await chatWithAITemplate(updatedMessages);

      if (response?.createdDocument === true && response?.data) {
        const result = await buildTemplateParamsFromResponse(response.data);

        if (result?.documentId) {
          const { documentId, filePath, fileName, params } = result;
          if (filePath) {
            setGeneratedDocumentInfo({ documentId, filePath, fileName });
          }
          setGeneratedDocument({ params });
          setMessages((prev) => [
            ...prev,
            {
              role: "assistant",
              content:
                "Your document has been created. You can view it or continue to configure the template below.",
            },
          ]);
          toast.success("Document created from conversation.");
        } else {
          toast.error("Document creation failed. Please try again.");
          setMessages((prev) => [
            ...prev,
            {
              role: "assistant",
              content:
                "Sorry, the document could not be created. Please try again or add more details and say **Create document** again.",
            },
          ]);
        }
      } else {
        const assistantMessage = response?.message;
        const content =
          assistantMessage && typeof assistantMessage === "object" && assistantMessage.content
            ? assistantMessage.content
            : typeof assistantMessage === "string"
            ? assistantMessage
            : "I've noted that. You can add more details or say **Create document** when you're ready.";
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content },
        ]);
      }
    } catch (error) {
      const errMsg = error?.message || error?.error || "Something went wrong. Please try again.";
      toast.error(errMsg);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Sorry, I couldn't process that. Please try again or say **Create document** when you're ready to generate.",
        },
      ]);
    } finally {
      setIsSending(false);
    }
  };

  const handleCreateDocumentClick = async () => {
    if (isSending) return;

    if (usage?.exceededTemplateCreation) {
      toast.error(
        "You have reached the template creation limit for your plan. Please upgrade to create more templates."
      );
      return;
    }

    const command = "Create document";
    const newUserMessage = { role: "user", content: command };
    const updatedMessages = [...messages, newUserMessage];
    setMessages(updatedMessages);
    setIsSending(true);

    try {
      const response = await chatWithAITemplate(updatedMessages);

      if (response?.createdDocument === true && response?.data) {
        const result = await buildTemplateParamsFromResponse(response.data);

        if (result?.documentId) {
          const { documentId, filePath, fileName, params } = result;
          if (filePath) {
            setGeneratedDocumentInfo({ documentId, filePath, fileName });
          }
          setGeneratedDocument({ params });
          setMessages((prev) => [
            ...prev,
            {
              role: "assistant",
              content:
                "Your document has been created. You can view it or continue to configure the template below.",
            },
          ]);
          toast.success("Document created from conversation.");
        } else {
          toast.error("Document creation failed. Please try again.");
          setMessages((prev) => [
            ...prev,
            {
              role: "assistant",
              content:
                "Sorry, the document could not be created. Please try again or add more details and say **Create document** again.",
            },
          ]);
        }
      } else {
        const assistantMessage = response?.message;
        const content =
          assistantMessage && typeof assistantMessage === "object" && assistantMessage.content
            ? assistantMessage.content
            : typeof assistantMessage === "string"
            ? assistantMessage
            : "I've noted that. You can add more details or say **Create document** when you're ready.";
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content },
        ]);
      }
    } catch (error) {
      const errMsg = error?.message || error?.error || "Something went wrong. Please try again.";
      toast.error(errMsg);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Sorry, I couldn't process that. Please try again or say **Create document** when you're ready to generate.",
        },
      ]);
    } finally {
      setIsSending(false);
    }
  };

  const handleViewDocument = async () => {
    if (!generatedDocumentInfo?.filePath) {
      toast.error("Document path not available");
      return;
    }
    try {
      const signedUrl = await getSignedUrl(generatedDocumentInfo.filePath);
      window.open(signedUrl, "_blank");
    } catch (error) {
      console.error("Error fetching signed URL:", error);
      toast.error("Failed to open document. Please try again.");
    }
  };

  const handleDownloadDocument = async () => {
    if (!generatedDocumentInfo?.filePath) {
      toast.error("Document path not available");
      return;
    }
    try {
      const signedUrl = await getSignedUrl(generatedDocumentInfo.filePath);
      const response = await fetch(signedUrl);
      if (!response.ok) throw new Error("Failed to fetch document");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = generatedDocumentInfo.fileName || "document.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Document downloaded");
    } catch (error) {
      console.error("Error downloading document:", error);
      toast.error("Failed to download document. Please try again.");
    }
  };

  const handleContinueToTemplate = () => {
    if (generatedDocument?.params) {
      navigate(`/dashboard/templates/create?${generatedDocument.params}`);
    }
  };

  const handleClear = () => {
    setMessages([]);
    setInput("");
    setGeneratedDocument(null);
    setGeneratedDocumentInfo(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const suggestedStarts = [
    "I need an NDA for a software project",
    "Create an employment contract for a developer",
    "I want a service agreement between client and consultant",
  ];

  // Set header in Navbar (centered with content)
  useEffect(() => {
    setHeaderContent(
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 w-full">
        <div className="min-w-0">
          <BreadCrumb
            items={[
              { label: "Dashboard", href: "/dashboard" },
              { label: "Templates", href: "/dashboard/templates" },
              { label: "Create with AI" },
            ]}
            className="mb-2"
          />
          <h1 className="font-heading text-lg sm:text-xl font-bold text-gray-900">
            Create with AI
          </h1>
          <p className="text-xs text-gray-500 truncate max-w-[280px] sm:max-w-none">
            Describe your document, then say &quot;Create document&quot;
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {generatedDocument && generatedDocumentInfo && (
            <>
              <div className="hidden sm:flex items-center gap-1 rounded-lg bg-gray-100 p-0.5">
                <button
                  type="button"
                  onClick={handleViewDocument}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-700 rounded-md hover:bg-white hover:shadow-sm transition-colors"
                >
                  View
                </button>
                <button
                  type="button"
                  onClick={handleDownloadDocument}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-700 rounded-md hover:bg-white hover:shadow-sm transition-colors"
                >
                  Download
                </button>
              </div>
              <button
                type="button"
                onClick={handleContinueToTemplate}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold text-white bg-primary rounded-lg hover:opacity-90 shadow-sm"
              >
                Continue to configure
              </button>
              <span className="h-4 w-px bg-gray-200" aria-hidden />
            </>
          )}
          <button
            type="button"
            onClick={() => setHistoryOpen(true)}
            className="inline-flex items-center gap-1.5 px-2.5 py-2 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            History
          </button>
          {(messages.length > 0 || generatedDocument) && (
            <button
              type="button"
              onClick={handleClear}
              className="inline-flex items-center gap-1.5 px-2.5 py-2 text-xs font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            >
              New chat
            </button>
          )}
        </div>
      </div>
    );
    return () => setHeaderContent(null);
  }, [
    setHeaderContent,
    generatedDocument,
    generatedDocumentInfo,
    messages.length,
  ]);

  return (
    <PagePermissionGuard
      module="templates"
      action="write"
      pageName="AI Template Creation"
    >
      <div className="flex flex-col min-h-[calc(100vh-140px)] w-full">
        {/* Conversation history panel */}
        {historyOpen && (
          <>
            <div
              className="fixed inset-0 bg-black/30 z-40"
              aria-hidden
              onClick={() => setHistoryOpen(false)}
            />
            <div className="fixed top-0 right-0 bottom-0 z-50 w-full max-w-md bg-white shadow-xl flex flex-col border-l border-gray-200">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Conversation history</h2>
                <button
                  type="button"
                  onClick={() => setHistoryOpen(false)}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                  aria-label="Close"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                {loadingHistory ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                      <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: "150ms" }} />
                      <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: "300ms" }} />
                    </div>
                  </div>
                ) : conversations.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-8">No completed conversations yet.</p>
                ) : (
                  <ul className="space-y-2">
                    {conversations.map((conv) => (
                      <li
                        key={conv._id}
                        className="block p-3 rounded-lg border border-gray-200 bg-gray-50/50 hover:bg-gray-50 hover:border-gray-300 transition-colors"
                      >
                        <button
                          type="button"
                          onClick={() => {
                            setHistoryOpen(false);
                            navigate(`/dashboard/templates/ai-conversations/${conv._id}`);
                          }}
                          className="text-left w-full"
                        >
                          <p className="text-sm font-medium text-gray-900 line-clamp-2">{conv.title || "Untitled conversation"}</p>
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                              {conv.status || "completed"}
                            </span>
                            <span className="text-xs text-gray-500">
                              {conv.messageCount ?? 0} messages
                            </span>
                            <span className="text-xs text-gray-400">
                              {formatHistoryDate(conv.updatedAt || conv.createdAt)}
                            </span>
                          </div>
                        </button>
                        {conv.documentId && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setHistoryOpen(false);
                              const params = new URLSearchParams();
                              params.set("documentId", conv.documentId);
                              if (conv.title) params.set("aiTemplateName", conv.title);
                              navigate(`/dashboard/templates/create?${params.toString()}`);
                            }}
                            className="mt-2 text-xs font-medium text-primary hover:underline"
                          >
                            Use this document
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {!loadingHistory && historyPagination.totalPages > 1 && (
                <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-gray-200 bg-gray-50">
                  <button
                    type="button"
                    onClick={() => fetchConversations(historyPagination.page - 1)}
                    disabled={historyPagination.page <= 1}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeftIcon className="h-4 w-4" />
                    Previous
                  </button>
                  <span className="text-sm text-gray-600">
                    Page {historyPagination.page} of {historyPagination.totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => fetchConversations(historyPagination.page + 1)}
                    disabled={historyPagination.page >= historyPagination.totalPages}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                    <ChevronRightIcon className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          </>
        )}

        {/* Chat area: centered card, same UI as chat container */}
        <main className="flex-1 flex flex-col min-h-0 w-full px-4 py-4">
          <div className="w-full max-w-3xl mx-auto flex-1 flex flex-col min-h-0 rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
              {messages.length === 0 && !generatedDocument && (
                <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
                    <ChatBubbleLeftRightIcon className="h-7 w-7 text-primary" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-1">
                    Describe the document you need
                  </h2>
                  <p className="text-sm text-gray-500 max-w-sm mb-8">
                Tell me the type of document, parties involved, and any specific terms. When you’re ready, say <strong>Create document</strong> to generate it.
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                {suggestedStarts.map((text, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setInput(text)}
                    className="px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 border-0 rounded-lg transition-colors"
                  >
                    {text}
                  </button>
                ))}
                  </div>
                </div>
              )}

              {messages.length > 0 && (
                <div className="p-4 space-y-4 pb-8">
                  {(() => {
                    const lastAssistantIndex = messages.reduce(
                      (last, m, index) => (m.role === "assistant" ? index : last),
                      -1
                    );
                    return messages.map((msg, idx) => {
                      const showCreateButton =
                        !generatedDocument &&
                        msg.role === "assistant" &&
                        idx === lastAssistantIndex;

                      return (
                        <div
                          key={idx}
                          className={`flex gap-3 ${
                            msg.role === "user" ? "justify-end" : "justify-start"
                          }`}
                        >
                          {msg.role === "assistant" && (
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                              <SparklesIcon className="h-4 w-4 text-primary" />
                            </div>
                          )}
                          <div className="max-w-[85%] space-y-2">
                            <div
                              className={`rounded-2xl px-4 py-2.5 ${
                                msg.role === "user"
                                  ? "bg-primary text-white"
                                  : "bg-gray-100 border border-gray-200/80 text-gray-900"
                              }`}
                            >
                              <ChatMessageContent
                                content={msg.content}
                                className="text-sm break-words"
                              />
                            </div>
                            {showCreateButton && (
                              <div className="flex">
                                <button
                                  type="button"
                                  onClick={handleCreateDocumentClick}
                                  disabled={isSending}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border border-primary/30 text-primary bg-white hover:bg-primary/5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  <SparklesIcon className="h-3.5 w-3.5" />
                                  Create document
                                </button>
                              </div>
                            )}
                          </div>
                          {msg.role === "user" && (
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                              <UserCircleIcon className="h-5 w-5 text-gray-600" />
                            </div>
                          )}
                        </div>
                      );
                    });
                  })()}
              {isSending && (
                <div className="flex gap-3 justify-start">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <SparklesIcon className="h-4 w-4 text-primary" />
                  </div>
                  <div className="rounded-2xl px-4 py-3 bg-gray-100 border border-gray-200/80">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                      <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: "150ms" }} />
                      <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: "300ms" }} />
                    </div>
                  </div>
                </div>
              )}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            {/* Input bar - fixed at bottom of card */}
            <div className="flex-shrink-0 p-3 border-t border-gray-200 bg-gray-50/80">
              <div className="flex gap-2 bg-white border border-gray-200 rounded-xl p-2 shadow-sm">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={
                    generatedDocument
                      ? "Continue or say Create document to generate another..."
                      : "Describe your document or say Create document to generate..."
                  }
                  rows={1}
                  className="flex-1 resize-none px-3 py-2.5 border-0 focus:ring-0 focus:outline-none text-gray-900 placeholder-gray-400 text-sm rounded-lg min-h-[40px] max-h-28"
                  disabled={isSending}
                />
                <button
                  type="button"
                  onClick={handleSendMessage}
                  disabled={!input.trim() || isSending}
                  className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-lg bg-primary text-white disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
                >
                  <PaperAirplaneIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    </PagePermissionGuard>
  );
};

export default CreateAITemplate;
